import * as z from 'zod';

export const CreateCatalogImportRequest = z.object({
  catalogImport: z
    .object({
      _id: z
        .string()
        .describe('CatalogImport ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the CatalogImport was created.')
        .optional()
        .nullable(),
    })
    .describe('CatalogImport to be created.'),
});
export const CreateCatalogImportResponse = z.object({
  _id: z
    .string()
    .describe('CatalogImport ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the CatalogImport was created.')
    .optional()
    .nullable(),
});
export const AddProductsToImportRequest = z.object({
  catalogImportId: z
    .string()
    .describe('Catalog Import ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  products: z
    .array(
      z.object({
        product: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product with inventory.'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Product name. Translatable.')
                .min(1)
                .max(80)
                .optional()
                .nullable(),
              slug: z
                .string()
                .describe(
                  'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
                )
                .min(1)
                .max(300)
                .optional()
                .nullable(),
              plainDescription: z
                .string()
                .describe(
                  'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
              visible: z
                .boolean()
                .describe(
                  'Whether the product is visible to site visitors.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
              visibleInPos: z
                .boolean()
                .describe(
                  'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
                )
                .optional()
                .nullable(),
              media: z
                .object({
                  main: z
                    .intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this item's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                    .describe(
                      'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                    )
                    .optional(),
                  itemsInfo: z
                    .object({
                      items: z
                        .array(
                          z.intersection(
                            z.object({
                              altText: z
                                .string()
                                .describe('Image alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              displayName: z
                                .string()
                                .describe(
                                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                )
                                .max(80)
                                .optional()
                                .nullable(),
                              mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                              thumbnail: z
                                .object({
                                  url: z
                                    .string()
                                    .describe('Thumbnail url.')
                                    .url()
                                    .optional(),
                                  height: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail height.')
                                    .optional(),
                                  width: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail width.')
                                    .optional(),
                                  altText: z
                                    .string()
                                    .describe('Thumbnail alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                )
                                .optional(),
                              uploadId: z
                                .string()
                                .describe(
                                  'ID used to upload media to Wix Media Manager.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            }),
                            z.intersection(
                              z.xor([
                                z.object({
                                  _id: z.never().optional(),
                                  url: z.never().optional(),
                                }),
                                z.object({
                                  url: z.never().optional(),
                                  _id: z
                                    .string()
                                    .describe(
                                      'Set media by ID of an existing file in Wix Media Manager.'
                                    )
                                    .min(1)
                                    .max(200),
                                }),
                                z.object({
                                  _id: z.never().optional(),
                                  url: z
                                    .string()
                                    .describe(
                                      'Set media using an external media URL.'
                                    )
                                    .url(),
                                }),
                              ]),
                              z.xor([
                                z.object({
                                  image: z.never().optional(),
                                  video: z.never().optional(),
                                }),
                                z.object({
                                  video: z.never().optional(),
                                  image: z
                                    .string()
                                    .describe(
                                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this item's `id` field."
                                    ),
                                }),
                                z.object({
                                  image: z.never().optional(),
                                  video: z
                                    .string()
                                    .describe(
                                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                    ),
                                }),
                              ])
                            )
                          )
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                })
                .describe('Product media items.')
                .optional(),
              seoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            "Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages) —\ni.e. an entry from the site's Advanced/Custom Tags list.\n\nThis flag is scoped to that feature only. It is NOT an indicator of whether a standard tag\n(`title`, `description`, Open Graph, etc.) reflects a manually configured value versus a computed\ndefault/pattern — standard tags always resolve with `custom: false`, even when their content comes\nfrom a page's manually saved SEO title/description override. Don't use this field to verify whether\na live page reflects a manual per-page SEO edit.\n\nLimitation: when `ResolveStaticPageSeoTags` is called without `seoData`, the per-page SEO data is read\nonly from the Vibe/Wix-managed-headless override store. That store holds nothing for classic Wix Editor\nor Wix Studio pages, so even genuine Advanced/Custom Tags saved on those pages are missing from the\nresponse entirely — there, `false` means this API could not tell, not that no custom tag exists.\nPass `seoData` explicitly to resolve against a known set of tags."
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Product SEO data.')
                .optional(),
              taxGroupId: z
                .string()
                .describe(
                  '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              options: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Option name.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      optionRenderType: z
                        .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({ choicesSettings: z.never().optional() }),
                      z.object({
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this item's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe(
                                        'Choice name. For text choices, this field also contains the choice value.'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    inStock: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                      )
                                      .optional(),
                                    visible: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                      )
                                      .optional(),
                                    displayImage: z
                                      .string()
                                      .describe(
                                        'Image to display alongside the choice.\n\nThis field is returned only when the `PRODUCT_CHOICES_DISPLAY_IMAGE` requested field is included in the `fields` request parameter.\nThe returned image contains only its ID, URL, and alt text.'
                                      )
                                      .optional(),
                                    media: z
                                      .object({
                                        items: z
                                          .array(
                                            z.intersection(
                                              z.object({}),
                                              z.xor([
                                                z.object({
                                                  mediaId: z.never().optional(),
                                                  url: z.never().optional(),
                                                }),
                                                z.object({
                                                  url: z.never().optional(),
                                                  mediaId: z
                                                    .string()
                                                    .describe(
                                                      'Media ID from the Wix Media Manager. Will always be returned on read.'
                                                    )
                                                    .min(1)
                                                    .max(200),
                                                }),
                                                z.object({
                                                  mediaId: z.never().optional(),
                                                  url: z
                                                    .string()
                                                    .describe(
                                                      'External media URL. Can be used only on write.'
                                                    )
                                                    .url(),
                                                }),
                                              ])
                                            )
                                          )
                                          .max(15)
                                          .optional(),
                                      })
                                      .describe(
                                        'Media references for this choice.\n\nOn *write*, you can use either `media_id` or `url` to assign a media to this choice from the product\'s media.\nOn *read*, this field will return the media IDs of the associated media items.\nWhen this field is requested via the `PRODUCT_CHOICES_MEDIA_REFERENCES` field, the `linked_media` field will be returned empty to avoid duplication.\n\n> **Note:** Use this field instead of `linked_media`. To use it in your read flow, pass `"PRODUCT_CHOICES_MEDIA_REFERENCES"` to the `fields` array in your request.'
                                      )
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(1000)
                              .optional(),
                          })
                          .describe('Choices settings.'),
                      }),
                    ])
                  )
                )
                .max(6)
                .optional(),
              modifiers: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: MODIFIER`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Modifier title.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      modifierRenderType: z
                        .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .optional(),
                      mandatory: z
                        .boolean()
                        .describe(
                          'Whether customer input is required for this modifier.'
                        )
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z.never().optional(),
                      }),
                      z.object({
                        choicesSettings: z.never().optional(),
                        freeTextSettings: z
                          .object({
                            minCharCount: z
                              .number()
                              .int()
                              .describe('Minimum number of characters.')
                              .optional(),
                            maxCharCount: z
                              .number()
                              .int()
                              .describe('Maximum number of characters.')
                              .max(500)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount to be added to the product's price."
                              )
                              .optional()
                              .nullable(),
                            title: z
                              .string()
                              .describe(
                                'Title of the text to be input by the customer.'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                          })
                          .describe('Free text modifier settings.'),
                      }),
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this item's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    addedPrice: z
                                      .string()
                                      .describe('Added price.')
                                      .optional()
                                      .nullable(),
                                    displayImage: z
                                      .string()
                                      .describe(
                                        'Image to display alongside the choice.\n\nThis field is returned only when the `PRODUCT_CHOICES_DISPLAY_IMAGE` requested field is included in the `fields` request parameter.\nThe returned image contains only its ID, URL, and alt text.'
                                      )
                                      .optional(),
                                    media: z
                                      .object({
                                        items: z
                                          .array(
                                            z.intersection(
                                              z.object({}),
                                              z.xor([
                                                z.object({
                                                  mediaId: z.never().optional(),
                                                  url: z.never().optional(),
                                                }),
                                                z.object({
                                                  url: z.never().optional(),
                                                  mediaId: z
                                                    .string()
                                                    .describe(
                                                      'Media ID from the Wix Media Manager. Will always be returned on read.'
                                                    )
                                                    .min(1)
                                                    .max(200),
                                                }),
                                                z.object({
                                                  mediaId: z.never().optional(),
                                                  url: z
                                                    .string()
                                                    .describe(
                                                      'External media URL. Can be used only on write.'
                                                    )
                                                    .url(),
                                                }),
                                              ])
                                            )
                                          )
                                          .max(15)
                                          .optional(),
                                      })
                                      .describe(
                                        'Media references for this choice.\n\nOn *write*, you can use either `media_id` or `url` to assign a media to this choice from the product\'s media.\nOn *read*, this field will return the media IDs of the associated media items.\nWhen this field is requested via the `PRODUCT_CHOICES_MEDIA_REFERENCES` field, the `linked_media` field will be returned empty to avoid duplication.\n\n> **Note:** Use this field instead of `linked_media`. To use it in your read flow, pass `"PRODUCT_CHOICES_MEDIA_REFERENCES"` to the `fields` array in your request.'
                                      )
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(1000)
                              .optional(),
                          })
                          .describe('Choice settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
              brand: z
                .object({
                  _id: z
                    .string()
                    .describe('Brand ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Brand name.')
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product brand.\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
                )
                .optional(),
              infoSections: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Info section ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    uniqueName: z
                      .string()
                      .describe(
                        'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    description: z
                      .any()
                      .describe(
                        'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                      )
                      .optional(),
                    plainDescription: z
                      .string()
                      .describe(
                        'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                      )
                      .max(16000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(10)
                .optional(),
              ribbon: z
                .object({
                  _id: z
                    .string()
                    .describe('Ribbon ID.')
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Ribbon name.')
                    .max(30)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Primary product ribbon displayed on the product. Use `additionalRibbons` to assign further ribbons beyond the primary one.\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
                )
                .optional(),
              additionalRibbons: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Ribbon ID.')
                      .min(1)
                      .max(36)
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Ribbon name.')
                      .max(30)
                      .optional()
                      .nullable(),
                  })
                )
                .max(4)
                .optional(),
              mainCategoryId: z
                .string()
                .describe(
                  'The ID of the product\'s primary direct category, which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              productType: z.enum(['PHYSICAL', 'DIGITAL']).optional(),
              handle: z
                .string()
                .describe(
                  "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
                )
                .min(1)
                .max(100),
              variantsInfo: z
                .object({
                  variants: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether the variant is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          sku: z
                            .string()
                            .describe('Variant SKU (stock keeping unit).')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          barcode: z
                            .string()
                            .describe('Variant barcode.')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          price: z
                            .object({
                              actualPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  "Variant's current selling price. Must be greater than or equal to 0."
                                )
                                .optional(),
                              compareAtPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                                )
                                .optional(),
                              priceAfterDiscount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Variant selling price after automatic discounts are applied to `actualPrice`.\n\n> **Notes:**\n> + Returned only when you pass `"DISCOUNT_INFO"` in Products API requests.\n> + On Query Products and Search Products, returned only on `variantSummary.minPriceVariant` when `MIN_PRICE_VARIANT` is also requested.\n> + `formattedAmount` is returned only when you also pass `"CURRENCY"`.'
                                )
                                .optional(),
                            })
                            .describe('Variant price.')
                            .optional(),
                          revenueDetails: z
                            .object({
                              cost: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Item cost.')
                                .optional(),
                              profit: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                                )
                                .optional(),
                              profitMargin: z
                                .number()
                                .describe(
                                  'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                                )
                                .min(0)
                                .max(1)
                                .optional(),
                            })
                            .describe(
                              'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                            )
                            .optional(),
                          inventoryItem: z
                            .intersection(
                              z.object({
                                _id: z
                                  .string()
                                  .describe(
                                    'Inventory item ID.\nIf you want to update existing inventory item you must provide id,\notherwise new inventory item with different id will be created.'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                revision: z
                                  .string()
                                  .regex(
                                    /^-?\d+$/,
                                    'Must be a valid Int64 string'
                                  )
                                  .describe('Inventory item revision.')
                                  .optional()
                                  .nullable(),
                                preorderInfo: z
                                  .object({
                                    enabled: z
                                      .boolean()
                                      .describe(
                                        "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                                      )
                                      .optional()
                                      .nullable(),
                                    message: z
                                      .string()
                                      .describe(
                                        'Message displayed to customers when the item is out of stock and preorder is enabled.'
                                      )
                                      .min(1)
                                      .max(250)
                                      .optional()
                                      .nullable(),
                                    limit: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                                      )
                                      .min(0)
                                      .max(100000)
                                      .optional()
                                      .nullable(),
                                    counter: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                                      )
                                      .min(0)
                                      .max(99999)
                                      .optional()
                                      .nullable(),
                                    quantity: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                                      )
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item preorder info.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  inStock: z.never().optional(),
                                  quantity: z.never().optional(),
                                }),
                                z.object({
                                  quantity: z.never().optional(),
                                  inStock: z
                                    .boolean()
                                    .describe(
                                      'Tracking method - in stock.\n\nWhen set to `true`, the item is available for sale without a quantity limit.'
                                    ),
                                }),
                                z.object({
                                  inStock: z.never().optional(),
                                  quantity: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Tracking method - quantity left in inventory.\n\nQuantity can be negative when decrementing inventory for an order that has already been paid.'
                                    )
                                    .min(-99999)
                                    .max(99999),
                                }),
                              ])
                            )
                            .describe(
                              'Inventory item of the variant on the default location.'
                            )
                            .optional(),
                          choices: z
                            .array(
                              z.object({
                                optionChoiceIds: z
                                  .object({
                                    optionId: z
                                      .string()
                                      .describe('Option ID.')
                                      .min(1)
                                      .max(36)
                                      .optional(),
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                  })
                                  .describe('Option and choice IDs.')
                                  .optional(),
                                optionChoiceNames: z
                                  .object({
                                    optionName: z
                                      .string()
                                      .describe('Option name.')
                                      .min(1)
                                      .optional(),
                                    choiceName: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .optional(),
                                    renderType: z
                                      .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                      .optional(),
                                  })
                                  .describe('Option and choice names.')
                                  .optional(),
                              })
                            )
                            .min(0)
                            .max(6)
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z.never().optional(),
                          }),
                          z.object({
                            digitalProperties: z.never().optional(),
                            physicalProperties: z
                              .object({
                                weight: z
                                  .number()
                                  .describe(
                                    'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                                  )
                                  .min(0)
                                  .max(999999999.99)
                                  .optional()
                                  .nullable(),
                                pricePerUnit: z
                                  .object({
                                    settings: z
                                      .object({
                                        quantity: z
                                          .number()
                                          .describe(
                                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                          )
                                          .min(0.01)
                                          .max(999999999.99)
                                          .optional(),
                                        measurementUnit: z
                                          .enum([
                                            'UNSPECIFIED',
                                            'ML',
                                            'CL',
                                            'L',
                                            'CBM',
                                            'MG',
                                            'G',
                                            'KG',
                                            'MM',
                                            'CM',
                                            'M',
                                            'SQM',
                                            'OZ',
                                            'LB',
                                            'FLOZ',
                                            'PT',
                                            'QT',
                                            'GAL',
                                            'IN',
                                            'FT',
                                            'YD',
                                            'SQFT',
                                          ])
                                          .describe(
                                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                      )
                                      .optional(),
                                    value: z
                                      .string()
                                      .describe(
                                        "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                    priceAfterDiscount: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe(
                                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                          )
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Price per unit calculated from the variant automatic discount price.\n\n> **Notes:**\n> + Returned only when you pass `"DISCOUNT_INFO"` in Products API requests.\n> + `formattedAmount` is returned only when you also pass `"CURRENCY"`.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                                  )
                                  .optional(),
                                productDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Product dimensions (length, width, height of the physical product).'
                                  )
                                  .optional(),
                                packageDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Package dimensions (length, width, height of the shipping package).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Physical properties. Must be passed when `productType: PHYSICAL`'
                              ),
                          }),
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z
                              .object({
                                digitalFile: z
                                  .object({
                                    _id: z
                                      .string()
                                      .describe('Media ID in media manager.')
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                    fileName: z
                                      .string()
                                      .describe('Original file name.')
                                      .min(1)
                                      .max(1000)
                                      .optional(),
                                    fileSize: z
                                      .string()
                                      .describe('Original file size.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                    fileType: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'SECURE_PICTURE',
                                        'SECURE_VIDEO',
                                        'SECURE_DOCUMENT',
                                        'SECURE_MUSIC',
                                        'SECURE_ARCHIVE',
                                        'SECURE_RAW',
                                      ])
                                      .describe('File type.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Digital properties. Must be passed when `productType: DIGITAL`'
                              ),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(1000)
                    .optional(),
                })
                .describe(
                  "Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nVariants aren't auto-generated. You must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants)."
                )
                .optional(),
              subscriptionDetails: z
                .object({
                  subscriptions: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          title: z
                            .string()
                            .describe('Subscription title.')
                            .min(1)
                            .max(20)
                            .optional(),
                          description: z
                            .string()
                            .describe('Subscription description.')
                            .max(60)
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          frequency: z
                            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                            .optional(),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          discount: z
                            .intersection(
                              z.object({
                                type: z.enum(['AMOUNT', 'PERCENT']).optional(),
                              }),
                              z.xor([
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z.never().optional(),
                                }),
                                z.object({
                                  percentOff: z.never().optional(),
                                  amountOff: z
                                    .string()
                                    .describe(
                                      'Fixed amount subtracted from the variant price when calculating subscription prices.'
                                    ),
                                }),
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z
                                    .number()
                                    .describe(
                                      'Percentage subtracted from the variant price when calculating subscription prices.'
                                    )
                                    .min(0.1)
                                    .max(99.9),
                                }),
                              ])
                            )
                            .describe(
                              'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z.never().optional(),
                          }),
                          z.object({
                            billingCycles: z.never().optional(),
                            autoRenewal: z
                              .boolean()
                              .describe(
                                'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                              ),
                          }),
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z
                              .number()
                              .int()
                              .describe(
                                'Number of billing cycles before subscription ends.'
                              )
                              .min(2)
                              .max(999),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(6)
                    .optional(),
                  allowOneTimePurchases: z
                    .boolean()
                    .describe(
                      'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
                )
                .optional(),
              purchaseEligibility: z
                .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
                .describe('Purchase eligibility settings for the product.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the [app dashboard](https://dev.wix.com/dc3/my-apps/) before they can be accessed with API calls.'
                )
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  publicTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe('Tags')
                .optional(),
            }),
            z.xor([
              z.object({ physicalProperties: z.never().optional() }),
              z.object({
                physicalProperties: z
                  .object({
                    pricePerUnit: z
                      .object({
                        quantity: z
                          .number()
                          .describe(
                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                          )
                          .min(0.01)
                          .max(999999999.99)
                          .optional(),
                        measurementUnit: z
                          .enum([
                            'UNSPECIFIED',
                            'ML',
                            'CL',
                            'L',
                            'CBM',
                            'MG',
                            'G',
                            'KG',
                            'MM',
                            'CM',
                            'M',
                            'SQM',
                            'OZ',
                            'LB',
                            'FLOZ',
                            'PT',
                            'QT',
                            'GAL',
                            'IN',
                            'FT',
                            'YD',
                            'SQFT',
                          ])
                          .describe(
                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                          )
                          .optional(),
                      })
                      .describe(
                        'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                      )
                      .optional(),
                    fulfillerId: z
                      .string()
                      .describe('Fulfiller ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    shippingWeightRange: z
                      .object({
                        minValue: z
                          .number()
                          .describe(
                            'Minimum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                        maxValue: z
                          .number()
                          .describe(
                            'Maximum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                      })
                      .describe(
                        'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                      )
                      .optional(),
                    pricePerUnitRange: z
                      .object({
                        minValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Minimum price per unit across all variants.'
                          )
                          .optional(),
                        maxValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Maximum price per unit across all variants.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                      )
                      .optional(),
                    weightMeasurementUnitInfo: z
                      .object({
                        weightMeasurementUnit: z
                          .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                          .describe('Weight measurement unit.')
                          .optional(),
                      })
                      .describe(
                        'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    deliveryProfileId: z
                      .string()
                      .describe('Delivery profile ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
                  ),
              }),
            ])
          )
          .describe(
            'Product to upsert - handle field is required for identification'
          ),
        categoryIdsInfo: z
          .object({ categoryIds: z.array(z.string()).max(1000).optional() })
          .describe(
            'Categories to set for the product.\nOverrides any existing category assignments.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100),
});
export const AddProductsToImportResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                'The product `handle` as passed in the `AddProductsToImport` request.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const SubmitCatalogImportRequest = z.object({
  catalogImportId: z
    .string()
    .describe('Catalog Import ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const SubmitCatalogImportResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID for async operation tracking. Pass to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job) or [List Async Job Items](https://dev.wix.com/docs/api-reference/business-management/async-job/list-async-job-items) to retrieve details and metadata'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  totalProductsCount: z
    .number()
    .int()
    .describe('Total number of items that will be processed in this import')
    .optional(),
});
